import { Environment } from './enums/Environment';

// Environment detection
const getEnvironment = (): Environment => {
  const env = process.env.REACT_APP_ENVIRONMENT;
  
  if (env === 'production') return Environment.PRODUCTION;
  if (env === 'stage') return Environment.STAGE;
  return Environment.DEVELOP; // default
};

// API Configuration
const getApiConfig = () => {
  const env = getEnvironment();
  
  switch (env) {
    case Environment.PRODUCTION:
      return {
        baseUrl: process.env.REACT_APP_BASE_URL || 'https://ayvk7yg741.execute-api.eu-central-1.amazonaws.com',
        timeout: 30000,
        retries: 3,
      };
    case Environment.STAGE:
      return {
        baseUrl: process.env.REACT_APP_BASE_URL || 'https://stage-api.holifya.com',
        timeout: 20000,
        retries: 2,
      };
    default: // DEVELOP
      return {
        baseUrl: process.env.REACT_APP_BASE_URL || 'https://ayvk7yg741.execute-api.eu-central-1.amazonaws.com',
        timeout: 15000,
        retries: 1,
      };
  }
};

// Feature flags
const getFeatureFlags = () => {
  const env = getEnvironment();
  
  return {
    // Development features
    showVersionBadge: env === Environment.DEVELOP,
    showTestButtons: env === Environment.DEVELOP,
    enableDebugLogging: env === Environment.DEVELOP,
    
    // Production features
    enableAnalytics: env === Environment.PRODUCTION,
    enableErrorReporting: env === Environment.PRODUCTION,
    enablePerformanceMonitoring: env === Environment.PRODUCTION,
    
    // Environment-specific features
    enableUpdateDetection: env !== Environment.DEVELOP, // Only in stage/prod
    // Force update modal can be explicitly toggled via env var
    enableForceUpdate: process.env.REACT_APP_ENABLE_FORCE_UPDATE === 'true',
    enableServiceWorker: env === Environment.PRODUCTION,
  };
};

// App configuration
const getAppConfig = () => {
  const env = getEnvironment();
  
  return {
    name: 'Holifya',
    version: process.env.REACT_APP_VERSION || '2.6.0',
    environment: env,
    isDevelopment: env === Environment.DEVELOP,
    isProduction: env === Environment.PRODUCTION,
    isStage: env === Environment.STAGE,
    
    // URLs
    baseUrl: getApiConfig().baseUrl,
    appUrl: process.env.REACT_APP_URL || 'https://app.holifya.com',
    supportUrl: 'https://support.holifya.com',
    
    // Timeouts
    apiTimeout: getApiConfig().timeout,
    updateCheckInterval: 5 * 60 * 1000, // 5 minutes
    
    // Limits
    maxFileSize: 10 * 1024 * 1024, // 10MB
    maxUploadRetries: getApiConfig().retries,
    
    // Version management
    minimumRequiredVersion: '2.8.8', // Force update for versions below this
    forceUpdateMessage: 'Una nuova versione dell\'app è disponibile. Aggiorna per continuare.',
  };
};

// Export the main config
export const config = getAppConfig();

// Export individual configs for specific use cases
export const apiConfig = getApiConfig();
export const featureFlags = getFeatureFlags();

// Helper functions
export const isProduction = () => config.isProduction;
export const isDevelopment = () => config.isDevelopment;
export const isStage = () => config.isStage;

// Version comparison helper
export const compareVersions = (version1: string, version2: string): number => {
  const v1 = version1.split('.').map(Number);
  const v2 = version2.split('.').map(Number);
  
  for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
    const num1 = v1[i] || 0;
    const num2 = v2[i] || 0;
    
    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }
  
  return 0;
};

// Check if update is required
export const isUpdateRequired = (currentVersion: string, minimumRequiredVersion?: string): boolean => {
  const requiredVersion = minimumRequiredVersion || config.minimumRequiredVersion;
  return compareVersions(currentVersion, requiredVersion) < 0;
};

// Backward compatibility
export const BASE_URL = config.baseUrl;
export const ENVIRONMENT = config.environment;

// Type definitions
export interface ApiConfig {
  baseUrl: string;
  timeout: number;
  retries: number;
}

export interface FeatureFlags {
  showVersionBadge: boolean;
  showTestButtons: boolean;
  enableDebugLogging: boolean;
  enableAnalytics: boolean;
  enableErrorReporting: boolean;
  enablePerformanceMonitoring: boolean;
  enableUpdateDetection: boolean;
  enableForceUpdate: boolean;
  enableServiceWorker: boolean;
}

export interface AppConfig {
  name: string;
  version: string;
  environment: Environment;
  isDevelopment: boolean;
  isProduction: boolean;
  isStage: boolean;
  baseUrl: string;
  appUrl: string;
  supportUrl: string;
  apiTimeout: number;
  updateCheckInterval: number;
  maxFileSize: number;
  maxUploadRetries: number;
  minimumRequiredVersion: string;
  forceUpdateMessage: string;
} 